import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { TrackRow } from "@/components/TrackRow";
import { usePlayer } from "@/stores/player";
import { Music2, ChevronRight, Play } from "lucide-react";

export const Route = createFileRoute("/_authed/home")({
  head: () => ({ meta: [{ title: "Home — Sura" }] }),
  component: HomePage,
});

type Playlist = { id: string; name: string; description?: string; track_count?: number; tracks?: Track[] };
type Genre = { id: string; name: string };
type Track = { id: string; title: string; duration?: number; is_instrumental?: boolean; album?: { id: string; title: string }; album_title?: string; bpm?: number; key?: string };

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function HomePage() {
  const user = useAuth((s) => s.user);
  const [systemPL, setSystemPL] = useState<Playlist[]>([]);
  const [userPL, setUserPL] = useState<Playlist[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [history, setHistory] = useState<Track[]>([]);
  const [demoTracks, setDemoTracks] = useState<Track[]>([]);
  const playTrack = usePlayer((s) => s.playTrack);

  useEffect(() => {
    api.get("/api/playlists").then(({ data }) => {
      setSystemPL(data.systemPlaylists || []);
      setUserPL(data.userPlaylists || []);
    }).catch(() => {});
    api.get("/api/genres").then(({ data }) => {
      setGenres(data.genres || data || []);
    }).catch(() => {});
    api.get("/api/history?page=1&limit=5").then(({ data }) => {
      const items = data.history || data.items || data || [];
      setHistory(items.map((h: any) => h.track || h).filter(Boolean));
    }).catch(() => {});
    // Fetch demo tracks for landing page
    api.get("/api/tracks?page=1&limit=6").then(({ data }) => {
      const tracks = data.tracks || data.items || data || [];
      setDemoTracks(tracks);
    }).catch(() => {});
  }, []);

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 max-w-7xl mx-auto space-y-12">
      <section
        className="rounded-3xl p-8 md:p-12 relative overflow-hidden"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="relative">
          <p className="text-sm text-muted-foreground mb-2">{greeting()},</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight">{user?.full_name?.split(" ")[0] || "there"}</h1>
          <p className="mt-3 text-muted-foreground max-w-md">Your space sounds best with Sura. Pick a vibe to get started.</p>
        </div>
      </section>

      {systemPL.length > 0 && (
        <Section title="For your business">
          <Row>
            {systemPL.map((p) => <PlaylistCard key={p.id} playlist={p} />)}
          </Row>
        </Section>
      )}

      {genres.length > 0 && (
        <Section title="Genres">
          <div className="flex flex-wrap gap-2">
            {genres.map((g) => (
              <Link
                key={g.id}
                to="/albums"
                search={{ genre_id: g.id } as any}
                className="px-4 py-2 rounded-full bg-secondary text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                {g.name}
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Demo Music Section - Always visible for new users */}
      <Section title="Featured Demo Music">
        <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Listen to samples</h3>
            <Link
              to="/tracks"
              className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
            >
              Browse all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          {demoTracks.length > 0 ? (
            <div className="space-y-2">
              {demoTracks.slice(0, 4).map((t, i) => (
                <div
                  key={t.id}
                  onClick={() => playTrack(t, demoTracks)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary cursor-pointer transition-colors group"
                >
                  <button
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-primary text-primary-foreground group-hover:scale-110 transition-transform"
                  >
                    <Play className="h-3.5 w-3.5 fill-current ml-0.5" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{t.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {t.album?.title || t.album_title || "Track"} {t.is_instrumental ? " · Instrumental" : ""}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums w-12 text-right">
                    {t.duration ? `${Math.floor(t.duration / 60)}:${String(t.duration % 60).padStart(2, "0")}` : "—"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">Loading demo tracks...</div>
          )}
        </div>
      </Section>

      {userPL.length > 0 && (
        <Section title="Your Playlists">
          <Row>{userPL.map((p) => <PlaylistCard key={p.id} playlist={p} />)}</Row>
        </Section>
      )}

      {history.length > 0 && (
        <Section title="Recently Played">
          <div className="bg-card border border-border rounded-2xl p-2">
            {history.map((t, i) => <TrackRow key={`${t.id}-${i}`} track={t} index={i} queue={history} />)}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-4 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-2 scrollbar-thin">
      {children}
    </div>
  );
}

function colorFor(id: string) {
  const hues = [220, 250, 200, 180, 270, 230];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return hues[h % hues.length];
}

export function PlaylistCard({ playlist }: { playlist: Playlist }) {
  const hue = colorFor(playlist.id);
  return (
    <Link
      to="/playlist/$id"
      params={{ id: playlist.id }}
      className="shrink-0 w-44 md:w-52 group"
    >
      <div
        className="aspect-square rounded-2xl mb-3 flex items-center justify-center text-primary-foreground shadow-[var(--shadow-card)] group-hover:shadow-[var(--shadow-soft)] transition-shadow"
        style={{ background: `linear-gradient(135deg, oklch(0.65 0.16 ${hue}), oklch(0.5 0.18 ${hue + 30}))` }}
      >
        <Music2 className="h-10 w-10 opacity-80" />
      </div>
      <div className="font-medium text-sm truncate">{playlist.name}</div>
      <div className="text-xs text-muted-foreground">
        {playlist.track_count ?? playlist.tracks?.length ?? 0} tracks
      </div>
    </Link>
  );
}
